/**
 * Transport abstraction so game sessions don't care how bytes move. The game
 * ships with a PeerJS/WebRTC implementation (works from static hosting like
 * GitHub Pages) and tests use an in-memory one.
 */
export interface Connection {
  /** Stable id of the remote peer. */
  readonly id: string;
  send(message: unknown): void;
  onMessage(handler: (data: unknown) => void): void;
  onClose(handler: () => void): void;
  close(): void;
}

export interface HostEndpoint {
  readonly code: string;
  onConnection(handler: (connection: Connection) => void): void;
  /** Fires if the endpoint dies on its own (not when `close()` is called). */
  onLost(handler: (reason: string) => void): void;
  close(): void;
}

export interface NetworkProvider {
  host(code: string): Promise<HostEndpoint>;
  join(code: string): Promise<Connection>;
}

export type NetErrorKind = 'code-taken' | 'not-found' | 'unreachable' | 'unsupported' | 'timeout';

export class NetError extends Error {
  constructor(
    readonly kind: NetErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'NetError';
  }
}

const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 5;

export function generateRoomCode(random: () => number = Math.random): string {
  let code = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    code += CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)];
  }
  return code;
}

/** Uppercases and strips anything that can't appear in a room code. */
export function normalizeRoomCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, ROOM_CODE_LENGTH);
}

export function isValidRoomCode(code: string): boolean {
  return code.length === ROOM_CODE_LENGTH && [...code].every((c) => CODE_ALPHABET.includes(c));
}

/** Small helper implementing the handler bookkeeping shared by connections. */
export class ConnectionHandlers {
  private readonly messageHandlers: Array<(data: unknown) => void> = [];
  private readonly closeHandlers: Array<() => void> = [];
  /** Messages that arrived before anyone listened (e.g. an early "farm is full"). */
  private readonly pending: unknown[] = [];
  private closed = false;

  onMessage(handler: (data: unknown) => void): void {
    this.messageHandlers.push(handler);
    if (this.messageHandlers.length === 1 && this.pending.length > 0) {
      for (const data of this.pending.splice(0)) this.dispatch(data);
    }
  }

  onClose(handler: () => void): void {
    if (this.closed) handler();
    else this.closeHandlers.push(handler);
  }

  message(data: unknown): void {
    if (this.closed) return;
    if (this.messageHandlers.length === 0) {
      if (this.pending.length < 64) this.pending.push(data);
      return;
    }
    this.dispatch(data);
  }

  private dispatch(data: unknown): void {
    for (const handler of this.messageHandlers) {
      try {
        handler(data);
      } catch (error) {
        console.error('Petal Plains: message handler failed', error);
      }
    }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    for (const handler of this.closeHandlers) handler();
    this.closeHandlers.length = 0;
    this.messageHandlers.length = 0;
  }

  get isClosed(): boolean {
    return this.closed;
  }
}
